import { getLocalStorageItem, setLocalStorageItem } from "@/utils/storageUtil";

const SENT_TRANSACTIONS_STORAGE_KEY = "n32AbTastySentTransactions";
const MAX_REMEMBERED_TRANSACTIONS = 20;
const READY_POLL_INTERVAL_MS = 250;
const READY_TIMEOUT_MS = 10_000;

export interface AbTastyTransactionHit {
  tid: string;
  ta: string;
  tr: number;
  tt?: number;
  ts?: number;
  tc?: string;
  icn?: number;
  cv?: Record<string, string>;
}

export interface AbTastyItemHit {
  tid: string;
  in: string;
  ip?: number;
  iq?: number;
  ic?: string;
  iv?: string;
}

export type AbTastySendResult = "sent" | "duplicate" | "unavailable" | "failed";

const sentTransactionIds = new Set<string>();
const inFlightTransactionIds = new Set<string>();

function isAbTastyReady(): boolean {
  if (typeof window === "undefined" || typeof window.abtasty?.send !== "function") return false;
  const eventState = window.ABTasty?.eventState;
  return !eventState || eventState.trackingInitialized?.status === "complete";
}

export function waitForAbTasty(timeoutMs: number = READY_TIMEOUT_MS): Promise<boolean> {
  if (isAbTastyReady()) return Promise.resolve(true);
  if (typeof window === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    let attemptsLeft = Math.ceil(timeoutMs / READY_POLL_INTERVAL_MS);
    const interval = setInterval(() => {
      if (isAbTastyReady()) {
        clearInterval(interval);
        resolve(true);
      } else if (--attemptsLeft <= 0) {
        clearInterval(interval);
        resolve(false);
      }
    }, READY_POLL_INTERVAL_MS);
  });
}

function hasSentTransaction(tid: string): boolean {
  if (sentTransactionIds.has(tid)) return true;
  const remembered = getLocalStorageItem(SENT_TRANSACTIONS_STORAGE_KEY, []);
  return Array.isArray(remembered) && remembered.includes(tid);
}

function rememberSentTransaction(tid: string) {
  sentTransactionIds.add(tid);
  const remembered = getLocalStorageItem(SENT_TRANSACTIONS_STORAGE_KEY, []);
  const previous = Array.isArray(remembered) ? remembered.filter((id) => id !== tid) : [];
  setLocalStorageItem(SENT_TRANSACTIONS_STORAGE_KEY, [...previous, tid].slice(-MAX_REMEMBERED_TRANSACTIONS));
}

export async function sendAbTastyTransaction(
  hit: AbTastyTransactionHit,
  items: AbTastyItemHit[] = [],
): Promise<AbTastySendResult> {
  if (inFlightTransactionIds.has(hit.tid) || hasSentTransaction(hit.tid)) return "duplicate";

  inFlightTransactionIds.add(hit.tid);
  try {
    const abtasty = (await waitForAbTasty()) ? window.abtasty : undefined;
    if (!abtasty) {
      console.warn(
        "AB Tasty | The tag never loaded, so the transaction was not reported. There is likely an adblocker.",
      );
      return "unavailable";
    }
    abtasty.send("transaction", hit);
    rememberSentTransaction(hit.tid);

    try {
      items.forEach((item) => abtasty.send("item", item));
    } catch (error) {
      console.error("AB Tasty | Could not report the transaction's items: ", error);
    }
    return "sent";
  } catch (error) {
    console.error("AB Tasty | Could not report the transaction: ", error);
    return "failed";
  } finally {
    inFlightTransactionIds.delete(hit.tid);
  }
}

export async function sendAbTastyEvent(name: string): Promise<AbTastySendResult> {
  const abtasty = (await waitForAbTasty()) ? window.abtasty : undefined;
  if (!abtasty) return "unavailable";
  try {
    abtasty.send("event", { ec: "Action Tracking", ea: name });
    return "sent";
  } catch (error) {
    console.error(`AB Tasty | Could not report the "${name}" event: `, error);
    return "failed";
  }
}
